// CotationSelect.tsx

import { useState, useEffect, useMemo } from 'react'
import {
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  TextField,
  Chip,
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputLabel,
  CircularProgress,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { getCCAM, getNGAP } from '@state/thunks/procedureThunk'
import { CCAMDto, NGAPDto } from '@/services/dtos'

type CotationType = 'CCAM' | 'NGAP'

interface CotationSelectProps {
  selectedCcamCodes: string[]
  selectedNgapCodes: string[]
  onCcamChange: (codes: string[]) => void
  onNgapChange: (codes: string[]) => void
  examDate?: Date
}

export const CotationSelect = ({
  selectedCcamCodes,
  selectedNgapCodes,
  onCcamChange,
  onNgapChange,
  examDate = new Date(),
}: CotationSelectProps) => {
  const dispatch = useAppDispatch()
  const { ccam, ngap, ccamLoading } = useAppSelector((state) => state.procedure)
  const [cotationType, setCotationType] = useState<CotationType>('CCAM')
  const [inputValue, setInputValue] = useState('')

  // Fetch NGAP on mount (not search-based)
  useEffect(() => {
    if (ngap.length === 0) {
      dispatch(getNGAP())
    }
  }, [dispatch, ngap.length])

  useEffect(() => {
    if (cotationType !== 'CCAM' || !inputValue || inputValue.length < 2) {
      return
    }

    const timeoutId = setTimeout(() => {
      dispatch(getCCAM({ libelle: inputValue, examDate }))
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [dispatch, inputValue, examDate, cotationType])

  const selectedNgapItems = useMemo(() => {
    return ngap.filter((item) => selectedNgapCodes.includes(item.code ?? ''))
  }, [ngap, selectedNgapCodes])

  const handleCotationTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCotationType(event.target.value as CotationType)
    setInputValue('')
  }

  const handleCcamSelect = (_: unknown, value: CCAMDto | null) => {
    if (value && value.code && !selectedCcamCodes.includes(value.code)) {
      onCcamChange([...selectedCcamCodes, value.code])
    }
    setInputValue('')
  }

  const handleNgapSelect = (_: unknown, value: NGAPDto | null) => {
    if (value && value.code && !selectedNgapCodes.includes(value.code)) {
      onNgapChange([...selectedNgapCodes, value.code])
    }
    setInputValue('')
  }

  const handleRemoveCcam = (code: string) => {
    onCcamChange(selectedCcamCodes.filter((c) => c !== code))
  }

  const handleRemoveNgap = (code: string) => {
    onNgapChange(selectedNgapCodes.filter((c) => c !== code))
  }

  const [chapter, setChapter] = useState('')

  const handleChapter = (event: SelectChangeEvent) => {
    setChapter(event.target.value)
  }
  return (
    <Box>
      <FormControl sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
        <FormControl sx={{ mr: 1, minWidth: 120 }} size="small">
          <InputLabel id="demo-select-small-label">Chapitre</InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={chapter}
            label="Chapitre"
            onChange={handleChapter}
            size="small"
          >
            <MenuItem value={10}>Ten</MenuItem>
          </Select>
        </FormControl>
        <RadioGroup
          row
          value={cotationType}
          onChange={handleCotationTypeChange}
        >
          <FormControlLabel value="CCAM" control={<Radio />} label="CCAM" />
          <FormControlLabel value="NGAP" control={<Radio />} label="NGAP" />
        </RadioGroup>
      </FormControl>

      {cotationType === 'CCAM' ? (
        <Autocomplete
          options={ccam.filter(
            (item) => !selectedCcamCodes.includes(item.code ?? ''),
          )}
          value={null}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          onChange={handleCcamSelect}
          getOptionLabel={(option) => `${option.code} - ${option.libelle}`}
          loading={ccamLoading}
          filterOptions={(x) => x}
          noOptionsText={
            inputValue.length < 2
              ? 'Tapez au moins 2 caractères'
              : 'Aucun résultat'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Sélectionner CCAM"
              placeholder="Rechercher..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {ccamLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      ) : (
        <Autocomplete
          options={ngap.filter(
            (item) => !selectedNgapCodes.includes(item.code ?? ''),
          )}
          value={null}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          onChange={handleNgapSelect}
          getOptionLabel={(option) => `${option.code} - ${option.libelle}`}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Sélectionner NGAP"
              placeholder="Rechercher..."
            />
          )}
        />
      )}
      <Stack spacing={2} mt={2} direction="row">
        {selectedCcamCodes.length > 0 && (
          <Box width="50%">
            <Typography variant="subtitle2" color="textSecondary">
              CCAM
            </Typography>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              my={0.5}
              maxHeight={100}
              overflow="auto"
            >
              {selectedCcamCodes.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  onDelete={() => handleRemoveCcam(item)}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        )}
        {selectedNgapItems.length > 0 && (
          <Box width="50%">
            <Typography variant="subtitle2" color="textSecondary">
              NGAP
            </Typography>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              my={0.5}
              maxHeight={100}
              overflow="auto"
            >
              {selectedNgapItems.map((item) => (
                <Chip
                  key={item.id}
                  label={`${item.code} - ${item.libelle}`}
                  onDelete={() => handleRemoveNgap(item.code ?? '')}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  )
}
